import React from 'react';

import { SocialMediaItem } from '../../interfaces/socialMedia.types';
import { MenuPosition } from '../../interfaces/menu.types';
import { LanguageSlug } from '../../enums/languageSlug.enum';
import { PageType } from '../../enums/pageType.enum';
import { SocialMediaType } from '../../enums/socialMediaType.enum';
import { AlertBar } from '../alertBar/alertBar.component';
import { CompanyLogo } from '../companyLogo/companyLogo.component';
import { LogoVersion } from '../companyLogo/companyLogo.enums';
import { LanguageSwitch } from '../header/components/languageSwitch/languageSwitch.component';
import { SearchBox } from "../searchBox/searchBox.component";

import {
  Container,
  MenuColumn,
  MenuPositionsContainer,
  MenuPositionsColumn,
  MenuPositionItem,
  BottomContainer,
  SocialIconContainer,
  SliderColumn,
  SocialIcon,
  InnerContainer,
  MenuHeader,
  CompanyLogoContainer,
  LocalIconInCircleWithLabel,
  LogoWithCloseIconContainer,
  CloseIconWithMenuContainer,
  CloseIcon,
  SocialIconsContainer,
} from './fullscreenMenu.styled';
import {
  renderWhenTrueOr,
  renderInternalLink,
  renderExternalLink,
  renderWhenTrue,
} from '../../utils';
import { FulscreenMenuData } from './fullscreenMenu.types';
import { PhotoSlider } from './components/photoSlider/photoSlider.component';

import FbIcon from './images/facebook.svg';
import YoutubeIcon from './images/youtube.svg';
import LinkedInIcon from './images/linkedin.svg';

export const FullscreenMenu: React.FC<FulscreenMenuData> = (data: FulscreenMenuData) => {
  const {
    menuData,
    sliderData,
    isMenuShown,
    socialMediaData,
    hideExpandedMenu,
    currentLanguage,
    alertContent,
    logoData,
    languages,
    setCurrentLanguage,
    hasTextContentInAlertBar,
    alertBarLogo,
    searchSettings,
    hasLogoInAlertBar,
  } = data;
  const linkForLanguageButton = `/${currentLanguage === LanguageSlug.Pl ? '' : currentLanguage}`;

  const renderMenuPositions = (columnNumber: number) => menuData
    .filter((menuPosition: MenuPosition) => parseInt(menuPosition.columnNumber) === columnNumber)
    .map((menuPosition: MenuPosition) => {
      const renderLink = renderWhenTrueOr(
        () => renderInternalLink(menuPosition.label, menuPosition.internalPage, currentLanguage),
        () => renderExternalLink(menuPosition.label, menuPosition.externalPage)
      );
      const currentPath = typeof window !== 'undefined' ? window.location.href : '';
      const isActivePosition = currentPath.includes(menuPosition.internalPage?.post_name);

      return (
        <MenuPositionItem
          key={menuPosition.label}
          onClick={() => hideExpandedMenu(false)}
          isActive={isActivePosition}
        >
          {renderLink(menuPosition.positionType === PageType.Internal)}
        </MenuPositionItem>
      );
    });

  const getSocialMediaIcon = (socialMediaType: SocialMediaType) => {
    switch (socialMediaType) {
      case SocialMediaType.Facebook:
        return FbIcon;
      case SocialMediaType.Youtube:
        return YoutubeIcon;
      case SocialMediaType.LinkedIn:
        return LinkedInIcon;
    }
  };

  const renderSocialIcons = () => socialMediaData.map((socialMediaItem: SocialMediaItem) => (
    <SocialIconContainer key={socialMediaItem.url} href={socialMediaItem.url} target='_blank'>
      <SocialIcon src={getSocialMediaIcon(socialMediaItem.type)} alt={socialMediaItem.type} />
    </SocialIconContainer>
  ));

  const renderPhotoSlider = renderWhenTrue(() => <PhotoSlider sliderData={sliderData} />);

  const hideMenu = () => hideExpandedMenu(false);

  return (
    <Container isVisible={isMenuShown}>
      <AlertBar
        alertContent={alertContent}
        hasTextContent={hasTextContentInAlertBar}
        hasLogoInBanner={hasLogoInAlertBar}
        logo={alertBarLogo}
      />
      <MenuHeader>
        <LogoWithCloseIconContainer>
          <LocalIconInCircleWithLabel
            label='menu'
            onClick={hideMenu}
          >
            <CloseIcon />
          </LocalIconInCircleWithLabel>
          <CompanyLogoContainer>
            <CompanyLogo
              target={linkForLanguageButton}
              logoData={logoData}
              version={LogoVersion.WithColor}
            />
          </CompanyLogoContainer>
        </LogoWithCloseIconContainer>
        <LanguageSwitch
          languages={languages}
          currentLanguage={currentLanguage}
          setCurrentLanguage={setCurrentLanguage}
        />
      </MenuHeader>
      <InnerContainer>
        <MenuColumn>
          <CloseIconWithMenuContainer>
            <LocalIconInCircleWithLabel
              label='menu'
              onClick={() => hideExpandedMenu(false)}
            >
              <CloseIcon />
            </LocalIconInCircleWithLabel>
            <MenuPositionsContainer>
              <MenuPositionsColumn>
                {renderMenuPositions(1)}
              </MenuPositionsColumn>
              <MenuPositionsColumn>
                {renderMenuPositions(2)}
              </MenuPositionsColumn>
            </MenuPositionsContainer>
          </CloseIconWithMenuContainer>
          <BottomContainer>
            <SearchBox
              currentLanguage={currentLanguage}
              onSubmit={hideMenu}
              placeholder={searchSettings.searchPlaceholder}
            />
            <SocialIconsContainer>
              {renderSocialIcons()}
            </SocialIconsContainer>
          </BottomContainer>
        </MenuColumn>
        <SliderColumn>
          {renderPhotoSlider(isMenuShown)}
        </SliderColumn>
      </InnerContainer>
    </Container>
  );
}
